'use client';
import styles from './chatMessageDetail.module.scss';
import { IoIosArrowForward } from 'react-icons/io';
import { BiDotsVerticalRounded } from 'react-icons/bi';
import { Alert, Dropdown, Result, Spin } from 'antd';
import React, { useContext, useEffect, useState } from 'react';
import { ChatType, MessageStatusType, MessageType, PaginationType, ThemeEnum } from 'types';
import MessageCardComp from './messageCard/messageCard.index';
import { COLORS } from 'utilities';
import { GlobalContext } from 'context';
import { RestAPI } from 'scripts';
import { API, PATH } from 'data';
import Image from 'next/image';
import CustomInputComp from '../customInput/customInput.index';
import PostReportComp from './report/report.index';
import { useRouter } from 'next/navigation';

function ChatMessageDetailComp({
    data,
    onBack,
    mode,
    onSent,
    onReport,
}: {
    data?: ChatType;
    onBack: () => void;
    mode: 'sent' | 'received';
    onSent: (e: MessageType) => void;
    onReport: () => void;
}) {
    const { theme, profile, chatDeactivateMessage } = useContext(GlobalContext);
    const [messages, setMessages] = useState<MessageType[]>();
    const [error, setError] = useState<string>();
    const [report, setReport] = useState(false);
    const router = useRouter();

    useEffect(() => {
        setMessages(undefined);
        getMessages();
        const temp = setInterval(getMessages, 4 * 1000);
        return () => {
            clearInterval(temp);
        };
    }, [data?.id]);

    function getMessages() {
        if (data?.id) {
            RestAPI.get<PaginationType<MessageType>>(API.chat.messages, { chat: data.id, page: 1, page_size: 300 })
                .then((res) => {
                    setMessages(res.data.data);
                    if (
                        res.data.data.filter((i) => i.status === MessageStatusType.unread && i.user.id !== profile?.id)
                            .length
                    ) {
                        RestAPI.post(API.chat.seen(data.id), {});
                    }
                })
                .catch(() => setError('خطایی رخ داده است. لطفا مجددا تلاش نمایید'));
        }
    }

    const formatter = (date: string) => {
        const targetDate = new Date(date);
        const formattedDate = targetDate.toLocaleDateString('fa-IR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
        return formattedDate;
    };

    if (error?.length) return <Result status="error" title="خطا در بارگذاری" subTitle={error} />;
    if (!messages)
        return (
            <div className={`w-100 pv-9 center-content flex-1`}>
                <Spin />
            </div>
        );

    const avatar = mode === 'sent' ? data?.post.main_image?.file : data?.user.avatar?.file;
    const title = mode === 'sent' ? data?.post.title : data?.user.first_name + ' ' + data?.user.last_name;

    const chatItems: { date: string; messages: MessageType[] }[] = [];

    messages
        ?.sort((a, b) => b.id - a.id)
        .forEach((item) => {
            const lastOne = chatItems[chatItems.length - 1];
            if (!lastOne || new Date(item.created_at).toDateString() !== new Date(lastOne.date).toDateString()) {
                chatItems.push({ date: item.created_at, messages: [item] });
            } else {
                lastOne.messages.push(item);
                chatItems[chatItems.length - 1] = lastOne;
            }
        });

    return (
        <div className={styles['chat']}>
            <PostReportComp
                onClose={() => setReport(false)}
                chatID={report ? data?.id : undefined}
                onSubmit={() => {
                    setReport(false);
                    onReport();
                }}
            />
            <div className={`${styles['header-detail-chat']} center-content-y`}>
                <IoIosArrowForward
                    className="d-md-none-i pointer ml-2-i"
                    onClick={() => onBack()}
                    size={20}
                    color={theme === ThemeEnum.light ? COLORS.black1 : COLORS.gray2}
                />
                <div
                    className={`${styles['title-chat']} center-content-y flex-between p-1 w-100`}
                    onClick={() => {
                        router.push(PATH.postDetail(data?.post.slug ?? ''));
                    }}
                >
                    <div className="center-content-y g-1">
                        {avatar?.length ? (
                            <div className={`${styles['image']}`}>
                                <Image width={36} height={34} src={avatar} alt="" />
                            </div>
                        ) : null}
                        <p className="t-p1 t-400 t-l-black1">{title}</p>
                    </div>
                    {!data?.is_reported ? (
                        <div
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                            }}
                            className="pv-1 center-content ph-1"
                        >
                            <Dropdown
                                menu={{
                                    items: [
                                        {
                                            key: '1',
                                            danger: true,
                                            label: 'گزارش و مسدودسازی',
                                            onClick: () => setReport(true),
                                        },
                                    ],
                                }}
                            >
                                <BiDotsVerticalRounded
                                    size={20}
                                    color={theme === ThemeEnum.light ? COLORS.black1 : COLORS.gray2}
                                    className="pointer"
                                />
                            </Dropdown>
                        </div>
                    ) : null}
                </div>
            </div>
            <div
                style={{ padding: '140px 24px 86px 24px', width: '100%', height: '100%', overflowY: 'auto' }}
                className={styles['messages-box']}
            >
                {data?.is_reported ? (
                    <p className="t-400 t-p3 t-l-gray1 mv-2 text-center">این چت گزارش و مسدود شده است</p>
                ) : (
                    <>
                        {chatDeactivateMessage ? (
                            <Alert
                                message={<p className="t-p1 t-black-i t-600">قابلیت چت غیر فعال شده است</p>}
                                description={<p className="t-p2">{chatDeactivateMessage}</p>}
                                type="warning"
                                className="mb-2"
                                showIcon
                            />
                        ) : null}
                        {chatItems.length ? (
                            <>
                                {chatItems.map((msg) => (
                                    <React.Fragment key={msg.date}>
                                        <div className={`${styles['messages-box']} d-flex flex-column`}>
                                            {msg.messages.map((txt) => (
                                                <MessageCardComp
                                                    key={txt.id}
                                                    className={
                                                        profile?.id === txt.user.id
                                                            ? styles['me-message']
                                                            : styles['audience-message']
                                                    }
                                                    data={txt}
                                                />
                                            ))}
                                        </div>
                                        <p className="t-400 t-p3 t-l-gray1 mv-2 text-center">{formatter(msg.date)}</p>
                                    </React.Fragment>
                                ))}
                            </>
                        ) : chatDeactivateMessage ? null : (
                            <p className="t-p2 t-l-gray1 t-400 text-center">
                                شما تا اکنون پیامی ارسال نکرده اید. برای ارسال پیام شروع به تایپ کردن کنید.
                            </p>
                        )}
                        {chatDeactivateMessage ? null : (
                            <>
                                <Alert
                                    message={<p className="t-p2">هشدار پلتفرم تیک سبز</p>}
                                    description={
                                        <p className="t-p2">
                                            مسئول هرگونه معامله برعهده طرفین است و پلتفرم هیچگونه مسئولیتی در قبال آن
                                            ندارد درصورت مشاهده اطلاعات نادرست یا تخلف آگهی رو گزارش کنید
                                        </p>
                                    }
                                    type="warning"
                                    className="mb-2"
                                />
                                <Alert
                                    message={<p className="t-p2">هشدار پلیس فتا</p>}
                                    description={
                                        <p className="t-p2">
                                            لطفا پیش از انجام معامله و هر نوع پرداخت وجه ، از صحت کالا یا خدمات ارائه
                                            شده ، به صورت حضوری اطمینان حاصل نمایید
                                        </p>
                                    }
                                    type="info"
                                    className="mb-2"
                                />
                            </>
                        )}
                    </>
                )}
            </div>
            {!data?.is_reported && !chatDeactivateMessage ? (
                <div className={styles['input']}>
                    <CustomInputComp
                        chat={data}
                        onSubmit={(e) => {
                            setMessages([...messages, e]);
                            onSent(e);
                        }}
                    />
                </div>
            ) : null}
        </div>
    );
}

export default ChatMessageDetailComp;
